import Vue from 'vue';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import VueAxios from 'vue-axios';
import { app } from '@/main';
import { treatError } from '@/utils/index';
import eventBus from '@/utils/eventBus';
import segmentIo, {
  shouldShowTrackingDebugger,
  trackingDebuggerIsActive,
} from '@/utils/segment.io';
import { sendApiEventToHotjar } from '@/utils/hotjar';
import { USER_TIMEZONE } from '@/utils/DateUtils';

import ModalOptions from '@/components/Modal/ModalOptions';

import { IQueryPaginationOptions } from '@/models/general';
import { IHotjarEventType } from '@/models/hotjar-options';
import HTTPStatus from '@/constants/httpStatus';
import MaintenanceKeys from '@/constants/maintenance';
import logger from '@/utils/logger';
import { datadogLogs } from '@datadog/browser-logs';
import { isRemoteLoading, isCrossSiteEmbed } from '@/utils/remote-loading';

axios.defaults.withCredentials = true;

interface APIServiceConfig {
  disableToastMessage?: boolean;
}

const EMBED_TOKEN_KEY = '@twism:embed-token';

const hostname = window.location.hostname;
const domain = hostname.includes('twism.com')
  ? '.twism.com'
  : hostname.includes('twism.info')
    ? '.twism.info'
    : '';

const removeAppCookieWithAllDomains = () => {
  // Remove cookie with no domain (current subdomain only)
  document.cookie = `app=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;

  // Remove cookie with parent domain
  if (domain) {
    document.cookie = `app=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }

  // Remove cookie with current hostname
  document.cookie = `app=; path=/; domain=${hostname}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

export const setAppCookie = (appType: 'dashboard' | 'instore') => {
  removeAppCookieWithAllDomains();
  document.cookie = `app=${appType}; path=/; domain=${domain}`;
};

class APIService {
  static TOKEN_KEY: string = 'access_token';
  static SUPER_ADMIN: string = 'token';
  static PLATFORM_KEY = 'dashboard';

  static token: string = '';
  axios: AxiosInstance;
  interceptor!: number;

  constructor(private config?: APIServiceConfig) {
    Vue.use(VueAxios, axios);
    this.axios = axios.create();
    this.setEventBrowserDataOnHeader();
    this.setHeader();
    this.initAnonymousIdHeader();
    this.initAuthHeader();
    this.initInterception();
  }

  host() {
    return process.env.VUE_APP_API_URL;
  }

  servicesHost() {
    return process.env.VUE_APP_SERVICES_URL;
  }

  campaignHost() {
    return process.env.VUE_APP_CAMPAIGN_URL;
  }

  dashboardHost() {
    return process.env.VUE_APP_DASHBOARD_URL;
  }

  recaptchaSiteKey() {
    return process.env.VUE_APP_RECAPTCHA_SITE_KEY;
  }

  setEventBrowserDataOnHeader() {
    (async () => {
      const utmData = segmentIo.utmProperties();
      const deviceInfos = await segmentIo.getDeviceInfos();

      const eventBrowserData = {
        ...deviceInfos,
        ...utmData,
      };

      Vue.axios.defaults.headers.common['event-browser-data'] =
        JSON.stringify(eventBrowserData);
    })();
  }

  setHeader() {
    Vue.axios.defaults.headers.common['event-platform'] =
      APIService.PLATFORM_KEY;
    Vue.axios.defaults.headers.common['timezone'] = USER_TIMEZONE;
    Vue.axios.defaults.headers.common['timezone-offset'] =
      new Date().getTimezoneOffset();

    if (shouldShowTrackingDebugger())
      Vue.axios.defaults.headers.common['debugger-event'] =
        trackingDebuggerIsActive();

    // Auth is now cookie-based (HttpOnly). The browser sends the cookie
    // automatically via withCredentials: true (set below). No Bearer header needed.
    delete Vue.axios.defaults.headers.common.Authorization;
  }

  // Attaches the untrusted X-Analytics-Anonymous-Id correlation header per
  // request, reading the Segment anonymous id at request time (TWISM-17850).
  // Segment loads asynchronously AFTER this service is constructed, so a value
  // snapshotted in the constructor would miss every anonymous request; reading
  // per request (like the host's useFetcher) covers anonymous AND identified
  // flows. Never used for auth — the backend treats it as a hint only.
  initAnonymousIdHeader() {
    this.axios.interceptors.request.use((requestConfig) => {
      const anonymousId = segmentIo.getAnonymousId();
      if (anonymousId) {
        requestConfig.headers = requestConfig.headers || {};
        requestConfig.headers['X-Analytics-Anonymous-Id'] = anonymousId;
      }
      return requestConfig;
    });
  }

  getHeader() {
    return `Bearer ${APIService.token}`;
  }

  // TWISM-18411: true when `url` is on the same registrable domain as
  // VUE_APP_API_URL — the exact set the backend's SameSite auth cookie
  // (domain='.twism.info') reaches. A Bearer here stays inside the cookie's
  // trust boundary: it hits the main API and services-v2 (same
  // cookieOrBearerExtractor + secret), but not foreign hosts (e.g. nodo.cc)
  // the cookie never touched.
  static isEmbedBearerHost(url: string): boolean {
    const apiUrl = process.env.VUE_APP_API_URL || '';
    let apex = '';
    let reqHost = '';
    try {
      apex = new URL(apiUrl).hostname.split('.').slice(-2).join('.');
      reqHost = new URL(url).hostname;
    } catch (_e) {
      return false;
    }
    if (!apex || !reqHost) {
      return false;
    }
    return reqHost === apex || reqHost.endsWith(`.${apex}`);
  }

  // TWISM-18411: a cross-site provider iframe drops the SameSite=Lax cookie, so
  // attach the retained embed token (see setEmbedToken) as a Bearer on
  // Twism-domain hosts (isEmbedBearerHost).
  initAuthHeader() {
    this.axios.interceptors.request.use((requestConfig) => {
      const embedToken = APIService.getEmbedToken();
      if (embedToken && APIService.isEmbedBearerHost(requestConfig.url || '')) {
        requestConfig.headers = requestConfig.headers || {};
        requestConfig.headers.Authorization = `Bearer ${embedToken}`;
      }
      return requestConfig;
    });
  }

  disableInterception() {
    this.axios.interceptors.response.eject(this.interceptor);
  }

  get servicesDisabledRouteErrors() {
    return [
      '/business/signup/pre-validation',
      '/admin/customer-lead/wix/eligible-customers',
      '/auth/signin',
      '/manager/forgot-password/',
      '/admin/business-coin-setup/reserve',
      '/business/signup',
      '/v2/admin/business/sell-coins-settings',
      '/v2/location/suggestion-name/',
      '/admin/business-lead',
      '/smart-business-signup/offer-recommendation',
      '/smart-business-signup/generate-branded-coins',
    ];
  }

  get shouldDisableToastMessage() {
    return this.config && this.config.disableToastMessage;
  }

  initInterception() {
    this.interceptor = this.axios.interceptors.response.use(
      (response) => {
        const { hostname, pathname } = new URL(response.config.url || '');

        sendApiEventToHotjar({
          methodRequest: response.config.method!,
          typeEvent: IHotjarEventType.SUCCESS,
          hostApiRequest: hostname,
          pathnameApiRequest: pathname,
        });

        return response;
      },
      async (error) => {
        if (
          error.response &&
          error.response.status === HTTPStatus.SERVICE_UNAVAILABLE &&
          error.response.data
        ) {
          app.$router.push('/maintenance');

          localStorage.setItem(
            MaintenanceKeys.LAST_ROUTE_BEFORE_MAINTENANCE_STORAGE_KEY,
            app.$route.path,
          );

          return {
            status: HTTPStatus.SERVICE_UNAVAILABLE,
          };
        }

        const config = error.config || {};

        // Set the variable for the maximum number of retries
        config.retryCount = config.retryCount || 0;
        const maxRetryCount = (config.retry || {}).maxRetryCount || 2;

        if (
          (error.message && error.message.includes('timeout')) ||
          (error.message && error.message.includes('Network Error')) ||
          (error.response &&
            (error.response.status === 502 ||
              error.response.status === 503 ||
              error.response.status === 504) &&
            config.retryCount < maxRetryCount)
        ) {
          datadogLogs.logger.info(
            `Message: ${error.message}; status: ${
              error.response ? error.response.status : ''
            };`,
            {},
            error,
          );
          // Increase the retry count
          config.retryCount += 1;
          // Set the delay between retries using the backoff strategy
          const delay = (config.retry || {}).delay || 1300;
          const backoff = (config.retry || {}).backoff || 2;
          const delayTime = delay * Math.pow(backoff, config.retryCount);

          return new Promise((resolve) => {
            setTimeout(() => {
              resolve(this.axios(config));
            }, delayTime);
          });
        }

        const { hostname, pathname } = new URL(error.config.url || '');

        // /auth/me 403 is expected (unauthenticated probe — JwtV2Strategy
        // throws ForbiddenException when no cookie/token is present).
        // Skip the console.error so it isn't forwarded to Datadog.
        const isExpectedAuthMe403 =
          error.response &&
          error.response.status === HTTPStatus.FORBIDDEN &&
          pathname.includes('/auth/me');

        if (!isExpectedAuthMe403) {
          console.error('TWIM LOG: ', {
            url: pathname,
            status: error.response ? error.response.status : '',
            type: 'api',
            createdAt: Date.now(),
            content: error.response ? error.response.statusText : '',
            data: error.response ? error.response.data : '',
          });
        }

        if (pathname.match(/\/analytics\/|\/bi\//)) {
          return null;
        }

        if (pathname.match(/\/oauth\/api-token\//)) {
          throw error;
        }

        const sholdNotShowError = this.servicesDisabledRouteErrors.some(
          (path) => pathname.includes(path),
        );

        if (!error.response) {
          // No HTTP response (network failure / CORS / offline): reject instead
          // of resolving to null, so callers can catch it rather than silently
          // receiving null and crashing on `.data`. Every other failure mode
          // keeps its existing `return null` contract. See TWISM-17747.
          throw error;
        }

        sendApiEventToHotjar({
          methodRequest: error.response.config.method,
          typeEvent: IHotjarEventType.ERROR,
          hostApiRequest: hostname,
          pathnameApiRequest: pathname,
        });

        const execTreatError = (error: any) => {
          if (sholdNotShowError || this.shouldDisableToastMessage) return;

          treatError(error);
        };

        if (
          error.response &&
          error.response.status === HTTPStatus.UNAUTHORIZED &&
          !error.config.url.includes('auth/signin')
        ) {
          execTreatError(error);
          APIService.resetCredentials();

          return error;
        } else if (
          error.response &&
          error.response.status === HTTPStatus.FORBIDDEN
        ) {
          if (pathname.match(/\/admin\/special-offer\//)) {
            window.location.assign('/special-coins-offers');
            return;
          }
          // Auth-check call — no cookie present yet (or session expired).
          // Let the router guard handle the redirect; do not cascade into
          // the business-connect fetch which would loop.
          if (pathname.includes('/auth/me')) {
            return null;
          }
          app.$showLoaderWithLocker();
          try {
            const res = await this.get('/admin/business/connect');
            if (res.data[0]) {
              app.$hideLoader();
              eventBus.$emit(
                'openSubModal',
                new ModalOptions(
                  ``,
                  ``,
                  'none',
                  true,
                  ``,
                  ``,
                  ``,
                  false,
                  'LostAccessModal',
                  false,
                  true,
                  true,
                ),
                { data: res.data[0] },
              );
            } else {
              if (location.pathname !== '/manager')
                window.location.assign('/manager');
            }
          } catch (e) {
            APIService.resetCredentials();
          } finally {
            app.$hideLoader();
          }

          return null;
        }

        execTreatError(error);
        return error;
      },
    );
  }

  public static isSuperAdmin() {
    return window.location.pathname.includes('/admin');
  }

  public static readCredentials(_isSuperAdmin?: boolean) {
    // Auth state is now managed via HttpOnly cookies.
    // Token is no longer stored in localStorage.
    // withCredentials: true (line 26) ensures the cookie is sent automatically.
    APIService.token = '';
  }

  // TWISM-18411: the session JWT the Bearer interceptor sends when the cookie
  // cannot work (cross-site provider embed). Kept in sessionStorage — separate
  // from APIService.token (a cookie-auth presence flag the /auth/me handlers
  // overwrite) and surviving the full-page reload Login.vue does on sign-in.
  public static setEmbedToken(token: string | null) {
    try {
      if (token) {
        window.sessionStorage.setItem(EMBED_TOKEN_KEY, token);
      } else {
        window.sessionStorage.removeItem(EMBED_TOKEN_KEY);
      }
    } catch (_e) {
      // sessionStorage unavailable (blocked in some sandboxed iframes) — degrade to no Bearer.
    }
  }

  public static getEmbedToken(): string | null {
    try {
      return window.sessionStorage.getItem(EMBED_TOKEN_KEY);
    } catch (_e) {
      return null;
    }
  }

  /**
   * Exchanges a JWT received via URL (OAuth/provider callback) for an
   * HttpOnly auth cookie. Uses `GET /auth/switch-token/manager`, which
   * validates the incoming token, generates a fresh JWT and sets the cookie
   * via `authCookieOptions` on the backend.
   *
   * The original URL token is effectively burned: the cookie holds a newly
   * issued token that never appeared in logs/referrer/history.
   */
  public async exchangeUrlTokenForCookie(
    token: string,
  ): Promise<{ access_token: string; extraInfo: any }> {
    const { data } = await this.axios.get(
      `${this.host()}/auth/switch-token/manager`,
      {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      },
    );
    // Presence flag for auth derivation / existing cookie-auth machinery,
    // identical to the non-embed cookie flow.
    APIService.token = 'cookie-session';
    if (isCrossSiteEmbed()) {
      // TWISM-18411: inside a Wix/Shopify embed the browser rejects the
      // SameSite=Lax Set-Cookie of this response, so retain the fresh JWT the
      // endpoint also returns in the body and authenticate via Bearer instead.
      APIService.setEmbedToken((data && data.access_token) || token);
    }
    return data;
  }

  removeEmpty(obj: any) {
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != ''));
  }

  public static redirectToSignin() {
    const signinPath = this.isSuperAdmin() ? '/admin' : '/';
    window.location.assign(signinPath);
  }

  public static resetCredentials(callback?: () => void) {
    segmentIo.reset();
    logger.reset();

    if (isRemoteLoading()) {
      window.parent.postMessage({ type: 'auth:unauthorized' }, '*');
      return;
    }

    // Capture super admin state before clearing the token — the async logout
    // call may finish after the router has already navigated away, changing
    // window.location.pathname and making isSuperAdmin() return a wrong value.
    const signinPath = APIService.isSuperAdmin() ? '/admin' : '/';

    APIService.token = '';
    APIService.setEmbedToken(null);
    setAppCookie('dashboard');

    const redirect = () => {
      if (callback) {
        callback();
      } else {
        window.location.assign(signinPath);
      }
    };

    // Clear the HttpOnly auth cookie on the backend, then redirect.
    // Using then/catch so the redirect fires regardless of whether the
    // logout request succeeds (e.g., if the session is already expired).
    axios
      .post(`${process.env.VUE_APP_API_URL}/auth/logout`)
      .then(redirect)
      .catch(redirect);
  }

  setCredentials(_token: string, _isSuperAdmin?: boolean) {
    // Auth is now cookie-based — tokens are no longer stored in localStorage.
    // The backend sets HttpOnly cookies on signin; nothing to do here.
    this.setHeader();
  }

  getCredentials(_isSuperAdmin?: boolean) {
    // Auth is now cookie-based — tokens are not accessible via JavaScript.
    return null;
  }

  static getManagerJWTDecoded() {
    // Token is in an HttpOnly cookie — not accessible via JavaScript.
    // Use /auth/me to retrieve claims instead.
    return null;
  }

  get<T = any>(resource: string, config?: AxiosRequestConfig) {
    return this.axios.get<T>(this.host() + resource, config);
  }

  post<T = any>(resource: string, params?: object, headers?: object) {
    return this.axios.post<T>(this.host() + resource, params, headers);
  }

  put<T = any>(resource: string, params?: object) {
    return this.axios.put<T>(this.host() + resource, params);
  }

  patch(resource: string, params: object) {
    return this.axios.patch(this.host() + resource, params);
  }

  delete(resource: string, params?: object) {
    const config = params ? { data: params } : undefined;
    return this.axios.delete(this.host() + resource, config);
  }

  public generateResource(
    path: string,
    sortField: string,
    sortOrder: string = '',
    pagination: string = '',
    filters?: string,
    hasOldPagination?: boolean,
    defaultFilter?: string,
  ): string {
    let resource = path;
    if (pagination.includes(resource)) resource = pagination;
    else resource += pagination;

    if (sortField && !pagination.includes('sort'))
      resource += `sort=${sortField}`;

    if (hasOldPagination) {
      if (sortOrder && !pagination.includes('order'))
        resource += `&order=${sortOrder}`;
    } else if (sortOrder && !pagination.includes(':')) {
      resource += `:${sortOrder}`;
    }

    if (
      filters &&
      !pagination.includes('search') &&
      !pagination.includes('extras') &&
      !pagination.includes('filters')
    ) {
      resource += filters;
    }

    if (defaultFilter && !resource.includes(defaultFilter)) {
      resource += defaultFilter;
    }

    return resource;
  }

  public dynamicResource(data: any) {
    let attr = '';
    for (var key in data) {
      attr += `&${key}=${data[key]}`;
    }
    return attr;
  }

  public getQueryString(urlOrRecord: Record<string, string> | string): string {
    if (typeof urlOrRecord === 'string') {
      const query = urlOrRecord.split('?').pop(); // removes path
      return new URLSearchParams(query).toString();
    }
    return new URLSearchParams(urlOrRecord).toString();
  }

  public getQueryPagination<T = any>(
    url: string,
    {
      sortField = '',
      sortOrder = '',
      pagination = '',
      filters,
      period,
    }: Partial<IQueryPaginationOptions>,
  ) {
    let resource = this.generateResource(
      `${url}?`,
      sortField,
      sortOrder,
      pagination,
      filters,
    );

    if (period && !resource.includes('from')) {
      resource += this.dynamicResource(period);
    }

    return this.get<T>(resource);
  }
}

export default APIService;
